import React from 'react'
import { connect } from 'react-redux'
import { LOAD_WIZARD_BY_ID } from 'requests'
import {
  cloneWizard,
  archiveWizard,
  moveWizardToEmptyCategory,
  publishWizardToEmptyCategory
} from './FormManagerActions'
import Switch from 'components/Shell/Switch'
import ReactTooltip from 'react-tooltip'
import Portal from 'components/Portal/Portal'
import { ConfirmDialog } from 'components/Modal/Modal'
import ImportDialog from './ImportDialog'
import ImportIcon from '@tecuity/sos-icons/react/import'
import CaretUpIcon from '@tecuity/sos-icons/react/caret-up'
import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
import DraggableWizard from './DraggableWizard'
import Droppable from 'components/DragAndDrop/Droppable'
import FileDownloader from './FileDownloader'
import { noop } from 'utilities'

const getTitleWidth = (title = '') => {
  return (title.length + 1) * 13
}

let Category = ({
  title,
  id,
  filter,
  icon,
  uncategorized,
  wizards,
  canReorderUp,
  canReorderDown,
  visible,
  onVisibilityChange,
  onTitleChange,
  onReorderUp,
  onReorderDown,
  onRequestDelete,
  onRequestRefetch,
  cloneWizard,
  archiveWizard,
  moveWizardToEmptyCategory,
  publishWizardToEmptyCategory
}) => {
  const titleInput = React.useRef()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
  const [fileString, setFileString] = React.useState('')
  const [fileName, setFileName] = React.useState('')
  const downloader = React.useRef()
  const [downloadOnNextText, setDownloadOnNextTick] = React.useState(false)
  const [importDialogOpen, setImportDialogOpen] = React.useState(false)
  const [categoryTitle, setCategoryTitle] = React.useState(title)

  React.useEffect(() => {
    if(title && title !== categoryTitle){
      setCategoryTitle(title)
    }
  }, [title])

  const checkSubmit = e => {
    if (e.which === 13) {
      titleInput.current.blur()
    }
  }

  const exportJson = formDefinitionId => {
    LOAD_WIZARD_BY_ID(formDefinitionId, response => {
      let jsonString = JSON.stringify(response)
      setFileName(
        `${response.FORM_DEFINITION_ID} - ${response.TITLE.replace(/ of /g, ' ')
          .replace(/ and /g, ' ')
          .replace(/ the /g, ' ')}.json`
      )
      setFileString(jsonString)
      setDownloadOnNextTick(true)
    })
  }

  const filteredWizards = wizards.filter(wizard => wizard.TITLE.toLowerCase().includes(filter))
  const mappedWizards = filteredWizards.map((wizard, i) => {
    const next = i < filteredWizards.length - 1 ? filteredWizards[i + 1] : wizard
    return (
      <DraggableWizard
        draggable={!filter}
        droppable={!uncategorized}
        title={wizard.TITLE}
        description={wizard.DESCR}
        categoryId={id}
        formDefinitionId={wizard.FORM_DEFINITION_ID}
        lastModifiedDate={wizard.LAST_MODIFIED_DATE}
        lastModifiedName={wizard.LAST_MODIFIED_BY_NAME}
        onRequestExport={() => exportJson(wizard.FORM_DEFINITION_ID)}
        onRequestClone={values => cloneWizard(wizard.FORM_DEFINITION_ID, values.title)}
        onRequestArchive={() => archiveWizard(wizard.FORM_DEFINITION_ID)}
        next={next}
        icon={icon}
        key={wizard.FORM_DEFINITION_ID}
      />
    )
  })

  React.useEffect(() => {
    if (downloadOnNextText) {
      downloader.current.download()
      setDownloadOnNextTick(false)
    }
  }, [downloadOnNextText])

  const addToEmptyCategory = data => {
    if (data.categoryId !== 'uncategorized') {
      moveWizardToEmptyCategory({ origin: data, target: id })
    } else {
      publishWizardToEmptyCategory({ origin: data, target: id })
    }
  }

  if(!mappedWizards.length && filter && !uncategorized){
    return null;
  }

  return (
    <div className={`category editable ${uncategorized ? 'uncategorized' : ''}`}>
      <div className="title-wrapper">
        {!uncategorized && (
          <Switch
            onChange={onVisibilityChange}
            className="visibility-toggle"
            on={visible}
            tooltip="Toggle Visibility"
          />
        )}
        <h2 className="visually-hidden">{categoryTitle}</h2>
        {!uncategorized ? (
          <input
            className="title"
            ref={titleInput}
            value={categoryTitle}
            onChange={e => setCategoryTitle(e.target.value)}
            style={{
              width: getTitleWidth(categoryTitle)
            }}
            placeholder="Category Title"
            onFocus={() => titleInput.current.select()}
            onBlur={e => onTitleChange(categoryTitle)}
            onKeyPress={checkSubmit}
            aria-label="Category Title"
          />
        ) : (
          <h2 className="title">{categoryTitle}</h2>
        )}
        <div className="actions">
          {!uncategorized ? (
            <button
              className="btn btn-primary btn-text icon-wrapper delete"
              onClick={() => setDeleteConfirmOpen(true)}
              data-tip="Delete Category"
              data-for={id + 'd-c'}
              aria-label="Delete Category"
            >
              <span className="icon icon-cross"></span>
              <ReactTooltip effect="solid" place="top" id={id + 'd-c'} delayShow={700} />
            </button>
          ) : null}
          {!uncategorized ? (
            <button
              className="btn btn-primary btn-text icon-wrapper"
              onClick={() => setImportDialogOpen(true)}
              data-tip="Import into Category"
              data-for={id + 'imp'}
              aria-label="Import into Category"
            >
              <ImportIcon />
              <ReactTooltip effect="solid" place="top" id={id + 'imp'} delayShow={700} />
            </button>
          ) : null}
          {canReorderDown && !uncategorized ? (
            <button
              className="btn btn-primary btn-text down icon-wrapper"
              onClick={onReorderDown}
              data-tip="Move Down"
              data-for={id + 'r-down'}
              aria-label="Move Category Down"
            >
              <CaretDownIcon />
              <ReactTooltip effect="solid" place="top" id={id + 'r-down'} delayShow={700} />
            </button>
          ) : null}
          {canReorderUp && !uncategorized ? (
            <button
              className="btn btn-primary btn-text up icon-wrapper"
              onClick={onReorderUp}
              data-tip="Move Up"
              data-for={id + 'r-up'}
              aria-label="Move Category Up"
            >
              <CaretUpIcon />
              <ReactTooltip effect="solid" place="top" id={id + 'r-up'} delayShow={700} />
            </button>
          ) : null}
        </div>
        {deleteConfirmOpen && (
          <Portal isOpened={deleteConfirmOpen}>
            <ConfirmDialog
              closeModal={() => setDeleteConfirmOpen(false)}
              message={`Are you sure you want to delete the category: "${categoryTitle}"? This cannot be undone.`}
              modalStyle={{ borderRadius: 3 }}
              buttons={[
                { label: 'Cancel', onClick: noop, className: 'btn-default' },
                {
                  label: 'Delete Category',
                  className: 'btn-danger',
                  onClick: onRequestDelete,
                  fireAfterClose: true
                }
              ]}
            />
          </Portal>
        )}
      </div>
      {mappedWizards.length ? (
        <div className="form-blocks">{mappedWizards}</div>
      ) : (
        <Droppable
          accepts="WIZARD"
          onDrop={addToEmptyCategory}
          className="form-manager-end-spacer"
        />
      )}
      <FileDownloader
        fileString={fileString}
        fileName={fileName}
        contentType="text/json"
        ref={downloader}
      />
      <ImportDialog
        isOpened={importDialogOpen}
        closeModal={() => setImportDialogOpen(false)}
        categoryTarget={id}
        closeAndReload={() => {
          setImportDialogOpen(false)
          onRequestRefetch()
        }}
      />
    </div>
  )
}
export default connect(
  null,
  { cloneWizard, archiveWizard, moveWizardToEmptyCategory, publishWizardToEmptyCategory }
)(Category)



// WEBPACK FOOTER //
// ./src/components/FormManager/Category.js